import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  LinearProgress,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider
} from '@mui/material';
import {
  Upload as UploadIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayIcon,
  Send as SendIcon,
  Add as AddIcon,
  Email as EmailIcon,
  PersonOff as PersonOffIcon
} from '@mui/icons-material';
import hackathonService from '../../services/hackathonService';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

const HackathonDashboard = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);

  // Dialogs
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [moduleDialog, setModuleDialog] = useState(false);
  const [newModule, setNewModule] = useState({ name: '', description: '', complexity_level: 'medium' });

  const [kpiDialog, setKpiDialog] = useState(false);
  const [kpiConfigs, setKpiConfigs] = useState([]);

  const [scoreDialog, setScoreDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamScores, setTeamScores] = useState({});

  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [exportingFood, setExportingFood] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [exportingMembers, setExportingMembers] = useState(false);
  const [exportMembersResult, setExportMembersResult] = useState(null);

  const [sendingVolunteerInvites, setSendingVolunteerInvites] = useState(false);
  const [volunteerInviteResult, setVolunteerInviteResult] = useState(null);

  useEffect(() => {
    loadData();
  }, [eventId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [eventRes, statsRes] = await Promise.all([
        hackathonService.getEvent(eventId),
        hackathonService.getEventStats(eventId)
      ]);
      setEvent(eventRes.event);
      setStats(statsRes);
      setKpiConfigs(eventRes.event.kpi_configs || []);

      // Load registrations
      const regRes = await hackathonService.getRegistrations(eventId);
      setRegistrations(regRes.registrations || []);

      // Load teams
      const teamsRes = await hackathonService.getTeams(eventId);
      setTeams(teamsRes.teams || []);

      // Load volunteers
      const volRes = await hackathonService.getVolunteers(eventId);
      setVolunteers(volRes.volunteers || []);

      // Load leaderboard if event is in progress or completed
      if (['in_progress', 'judging', 'completed'].includes(eventRes.event.status)) {
        const lbRes = await hackathonService.getLeaderboard(eventId);
        setLeaderboard(lbRes);
      }
    } catch (err) {
      setError('Failed to load hackathon data');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      const result = await hackathonService.importParticipants(eventId, importFile, true);
      setImportResult(result);
      loadData(); // Refresh
      setImportDialog(false)
    } catch (err) {
      setError('Failed to import participants');
    } finally {
      setImporting(false);
    }
  };

  const handleGenerateTeams = async () => {
    try {
      setLoading(true);
      await hackathonService.generateTeams(eventId);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to generate teams');
    } finally {
      setLoading(false);
    }
  };

  // Update participant details (role, rating, experience)
  const handleUpdateParticipant = async (registrationId, field, value) => {
    try {
      const updates = { [field]: value };
      await hackathonService.updateRegistration(registrationId, updates);
      // Update local state to reflect change immediately
      setRegistrations(prev =>
        prev.map(reg =>
          reg.id === registrationId ? { ...reg, [field]: value } : reg
        )
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update participant');
    }
  };

  const handleAddModule = async () => {
    try {
      await hackathonService.addModule(eventId, newModule);
      setModuleDialog(false);
      setNewModule({ name: '', description: '', complexity_level: 'medium' });
      loadData();
    } catch (err) {
      setError('Failed to add module');
    }
  };

  const handleUpdateKPIs = async () => {
    try {
      await hackathonService.updateKPIs(eventId, kpiConfigs);
      setKpiDialog(false);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to update KPIs');
    }
  };

  const handleSyncTracking = async () => {
    try {
      setLoading(true);
      await hackathonService.syncAllTeamsTracking(eventId);
      loadData();
    } catch (err) {
      setError('Failed to sync tracking data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitScore = async () => {
    try {
      await hackathonService.submitTeamScore(selectedTeam.id, {
        kpi_scores: teamScores,
        comments: ''
      });
      setScoreDialog(false);
      setSelectedTeam(null);
      setTeamScores({});
      loadData();
    } catch (err) {
      setError('Failed to submit score');
    }
  };

  const handleFinalizeAwards = async () => {
    try {
      await hackathonService.finalizeAwards(eventId);
      loadData();
    } catch (err) {
      setError('Failed to finalize awards');
    }
  };

  const handleSendInvites = async (resendAll = false) => {
    try {
      setSendingInvites(true);
      setInviteResult(null);
      const result = await hackathonService.sendInvites(eventId, resendAll);
      setInviteResult(result);
      if (result.sent > 0) {
        loadData(); // Refresh to update invite_sent_at timestamps
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send invites');
    } finally {
      setSendingInvites(false);
    }
  };

  const handleSendVolunteerInvites = async (resendAll = false) => {
    try {
      setSendingVolunteerInvites(true);
      setVolunteerInviteResult(null);
      const result = await hackathonService.sendVolunteerInvites(eventId, resendAll);
      setVolunteerInviteResult(result);
      if (result.sent > 0) {
        loadData(); // Refresh to update invite_sent_at timestamps
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send volunteer invites');
    } finally {
      setSendingVolunteerInvites(false);
    }
  };  
  const handleExportFoodPreferences = async () => {
    try {
      setExportingFood(true);
      setExportResult(null);
      await hackathonService.exportFoodPreferences(eventId);
      setExportResult({ success: true, message: 'Food preferences exported successfully!' });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to export food preferences';
      setExportResult({ success: false, message: errorMsg });
    } finally {
      setExportingFood(false);
    }
  };

  const handleExportMembersByRole = async () => {
    try {
      setExportingMembers(true);
      setExportMembersResult(null);
      await hackathonService.exportMembersByRole(eventId);
      setExportMembersResult({ success: true, message: 'Members exported successfully!' });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to export members';
      setExportMembersResult({ success: false, message: errorMsg });
    } finally {
      setExportingMembers(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await hackathonService.updateEventStatus(eventId, newStatus);
      loadData();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'default',
      'registration_open': 'info',
      'registration_closed': 'warning',
      'team_formation': 'secondary',
      'in_progress': 'primary',
      'judging': 'warning',
      'completed': 'success'
    };
    return colors[status] || 'default';
  };

  if (loading && !event) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">{event?.name}</Typography>
          <Box display="flex" gap={1} mt={1}>
            <Chip label={event?.status?.replace('_', ' ')} color={getStatusColor(event?.status)} />
            <Chip
              label={`${new Date(event?.start_date).toLocaleDateString()} - ${new Date(event?.end_date).toLocaleDateString()}`}
              variant="outlined"
            />
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setKpiDialog(true)}
          >
            KPI Settings
          </Button>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={event?.status || ''}
              label="Status"
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="registration_open">Registration Open</MenuItem>
              <MenuItem value="registration_closed">Registration Closed</MenuItem>
              <MenuItem value="team_formation">Team Formation</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="judging">Judging</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Registrations</Typography>
              <Typography variant="h4">
                {stats?.registrations?.total || 0}
                <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                  ({stats?.registrations?.invited || 0} invited)
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats?.registrations?.confirmed || 0} confirmed, {stats?.registrations?.declined || 0} declined
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Teams</Typography>
              <Typography variant="h4">{stats?.teams?.total_teams || 0}</Typography>
              <Typography variant="body2">
                {stats?.teams?.modules_assigned || 0} modules assigned
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Volunteers</Typography>
              <Typography variant="h4">
                {stats?.volunteers?.total || 0}
                <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                  ({stats?.volunteers?.jury || 0} jury, {stats?.volunteers?.volunteers || 0} helpers)
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats?.volunteers?.confirmed || 0} confirmed, {stats?.volunteers?.declined || 0} declined
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Modules</Typography>
              <Typography variant="h4">{stats?.modules_count || 0}</Typography>
              <Typography variant="body2">{event?.modules?.length || 0} defined</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<PersonIcon />} label="Registrations" />
          <Tab icon={<GroupIcon />} label="Teams" />
          <Tab icon={<PersonIcon />} label="Volunteers" />
          <Tab icon={<TrophyIcon />} label="Scoring" />
          <Tab icon={<AssessmentIcon />} label="Tracking" />
        </Tabs>

        {/* Registrations Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Participants</Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={sendingInvites ? <CircularProgress size={20} /> : <EmailIcon />}
                  onClick={() => handleSendInvites(false)}
                  disabled={sendingInvites || registrations.filter(r => r.registration_status === 'invite').length === 0}
                >
                  Send Invites
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setImportDialog(true)}
                >
                  Import Excel
                </Button>
                <Button
                  variant="outlined"
                  startIcon={exportingFood ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleExportFoodPreferences}
                  disabled={exportingFood}
                >
                  Export Food Preferences
                </Button>
                <Button
                  variant="outlined"
                  startIcon={exportingMembers ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleExportMembersByRole}
                  disabled={exportingMembers}
                >
                  Export Members by Role
                </Button>
              </Box>
            </Box>
            {inviteResult && (
              <Alert
                severity={inviteResult.failed > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
                onClose={() => setInviteResult(null)}
              >
                {inviteResult.message}
                {inviteResult.errors?.length > 0 && (
                  <Box mt={1} fontSize="0.85em">
                    Errors: {inviteResult.errors.join(', ')}
                  </Box>
                )}
              </Alert>
            )}
            {exportResult && (
              <Alert
                severity={exportResult.success ? 'success' : 'warning'}
                sx={{ mb: 2 }}
                onClose={() => setExportResult(null)}
              >
                {exportResult.message}
              </Alert>
            )}
            {exportMembersResult && (
              <Alert
                severity={exportMembersResult.success ? 'success' : 'warning'}
                sx={{ mb: 2 }}
                onClose={() => setExportMembersResult(null)}
              >
                {exportMembersResult.message}
              </Alert>
            )}
            <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Name</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Email</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper', minWidth: 140 }}>Role</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper', minWidth: 100 }}>Rating</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper', minWidth: 120 }}>Experience</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Work Mode</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registrations.map((reg) => (
                    <TableRow key={reg.id}>
                      <TableCell>{reg.employee_name}</TableCell>
                      <TableCell>{reg.email}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={reg.operator_role || ''}
                          onChange={(e) => handleUpdateParticipant(reg.id, 'operator_role', e.target.value)}
                          sx={{ minWidth: 130, fontSize: '0.875rem' }}
                        >
                          <MenuItem value="dev_operator">Dev Operator</MenuItem>
                          <MenuItem value="fa_operator">FA Operator</MenuItem>
                          <MenuItem value="test_operator">Test Operator</MenuItem>
                          <MenuItem value="design_operator">Design Operator</MenuItem>
                          <MenuItem value="css_operator">CSS Operator</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={reg.ailyce_rating || 0}
                          onChange={(e) => handleUpdateParticipant(reg.id, 'ailyce_rating', parseFloat(e.target.value))}
                          sx={{ minWidth: 80, fontSize: '0.875rem' }}
                        >
                          {[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((val) => (
                            <MenuItem key={val} value={val}>{val}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={reg.years_of_experience || 0}
                          onChange={(e) => handleUpdateParticipant(reg.id, 'years_of_experience', parseFloat(e.target.value))}
                          sx={{ minWidth: 100, fontSize: '0.875rem' }}
                        >
                          {[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 12, 15, 20].map((val) => (
                            <MenuItem key={val} value={val}>{val} yrs</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={reg.registration_status}
                          color={reg.registration_status === 'confirmed' ? 'success' : reg.registration_status === 'declined' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{reg.work_mode}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Teams Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Teams</Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setModuleDialog(true)}
                >
                  Add Module
                </Button>
                <Button
                  variant="contained"
                  startIcon={<GroupIcon />}
                  onClick={handleGenerateTeams}
                  disabled={registrations.length < 5}
                >
                  Generate Teams
                </Button>
              </Box>
            </Box>

            {/* Modules */}
            {event?.modules?.length > 0 && (
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>Available Modules</Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {event.modules.map((m) => (
                    <Chip
                      key={m.module_id}
                      label={m.name}
                      color={m.assigned_team_id ? 'success' : 'default'}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Teams List */}
            <Grid container spacing={2}>
              {teams.map((team) => (
                <Grid item xs={12} md={6} key={team.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">{team.team_name}</Typography>
                        {team.rank && <Chip label={`#${team.rank}`} color="primary" />}
                      </Box>
                      {team.assigned_module && (
                        <Chip
                          label={team.assigned_module.name}
                          size="small"
                          color="success"
                          sx={{ mb: 1 }}
                        />
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Members: {team.members?.length || 0}
                      </Typography>
                      <Box mt={1}>
                        {team.senior_dev && (
                          <Chip size="small" label={`Senior: ${team.senior_dev.name || team.senior_dev.employee_name}`} sx={{ mr: 0.5, mb: 0.5 }} />
                        )}
                        {team.junior_devs?.map((junior, idx) => (
                          <Chip key={idx} size="small" label={`Junior: ${junior.name || junior.employee_name}`} sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                        {team.fa_operator && (
                          <Chip size="small" label={`FA: ${team.fa_operator.name || team.fa_operator.employee_name}`} sx={{ mr: 0.5, mb: 0.5 }} />
                        )}
                        {team.test_operator && (
                          <Chip size="small" label={`Test: ${team.test_operator.name || team.test_operator.employee_name}`} sx={{ mr: 0.5, mb: 0.5 }} />
                        )}
                      </Box>
                      {team.total_score > 0 && (
                        <Box mt={1}>
                          <Typography variant="body2">Score: {team.total_score?.toFixed(2)}</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(team.total_score, 100)}
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Unassigned Participants */}
            {(() => {
              const assignedIds = new Set();
              teams.forEach(team => {
                team.members?.forEach(m => assignedIds.add(m.id));
              });
              const unassigned = registrations.filter(r => !assignedIds.has(r.id) && !r.team_id);

              if (unassigned.length === 0) return null;

              return (
                <Box mt={4}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <PersonOffIcon color="warning" />
                    <Typography variant="h6">Unassigned Participants ({unassigned.length})</Typography>
                  </Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    These participants are not yet assigned to any team. Click "Generate Teams" to auto-assign them.
                  </Alert>
                  <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: 'background.paper' }}>Name</TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}>Role</TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}>Rating</TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}>Experience</TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {unassigned.map((reg) => (
                          <TableRow key={reg.id}>
                            <TableCell>{reg.employee_name}</TableCell>
                            <TableCell>
                              <Chip size="small" label={reg.operator_role?.replace('_', ' ') || 'N/A'} />
                            </TableCell>
                            <TableCell>{reg.ailyce_rating || 0}</TableCell>
                            <TableCell>{reg.years_of_experience?.toFixed(1) || 0} yrs</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={reg.registration_status}
                                color={reg.registration_status === 'confirmed' ? 'success' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })()}
          </Box>
        </TabPanel>

        {/* Volunteers Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Volunteers & Jury</Typography>
              <Button
                variant="outlined"
                startIcon={sendingVolunteerInvites ? <CircularProgress size={20} /> : <EmailIcon />}
                onClick={() => handleSendVolunteerInvites(false)}
                disabled={sendingVolunteerInvites || volunteers.filter(v => v.registration_status === 'invite' && !v.email?.includes('@placeholder.com')).length === 0}
              >
                Send Invites
              </Button>
            </Box>
            {volunteerInviteResult && (
              <Alert
                severity={volunteerInviteResult.failed > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
                onClose={() => setVolunteerInviteResult(null)}
              >
                {volunteerInviteResult.message}
                {volunteerInviteResult.errors?.length > 0 && (
                  <Box mt={1} fontSize="0.85em">
                    Errors: {volunteerInviteResult.errors.join(', ')}
                  </Box>
                )}
              </Alert>
            )}
            <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Name</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Email</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Type</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Task Area</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Assigned Role</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Invite Status</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Assignment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {volunteers.map((vol) => (
                    <TableRow key={vol.id}>
                      <TableCell>{vol.name}</TableCell>
                      <TableCell>{vol.email || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={vol.volunteer_type}
                          color={vol.volunteer_type === 'jury' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {vol.volunteer_type === 'volunteer' && vol.preferred_task_area ?
                          vol.preferred_task_area.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                          : '-'}
                      </TableCell>
                      <TableCell>{vol.assigned_role?.replace('_', ' ') || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={vol.registration_status || 'invite'}
                          color={
                            vol.registration_status === 'confirmed' ? 'success' :
                            vol.registration_status === 'invited' ? 'info' :
                            vol.registration_status === 'declined' ? 'error' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={vol.is_assigned ? 'Assigned' : 'Unassigned'}
                          color={vol.is_assigned ? 'success' : 'warning'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Scoring Tab */}
        <TabPanel value={activeTab} index={3}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Scoring & Leaderboard</Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleSyncTracking}
                >
                  Sync Tracking
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<TrophyIcon />}
                  onClick={handleFinalizeAwards}
                  disabled={event?.status !== 'judging'}
                >
                  Finalize Awards
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Rank</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Team</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Module</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Score</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(leaderboard?.teams || teams).map((team, idx) => (
                    <TableRow key={team.team_id || team.id}>
                      <TableCell>
                        {team.rank || idx + 1}
                        {team.rank === 1 && ' 🥇'}
                        {team.rank === 2 && ' 🥈'}
                        {team.rank === 3 && ' 🥉'}
                      </TableCell>
                      <TableCell>{team.team_name}</TableCell>
                      <TableCell>{team.module || team.assigned_module?.name || '-'}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography>{(team.total_score || 0).toFixed(2)}</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(team.total_score || 0, 100)}
                            sx={{ width: 100 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedTeam(team);
                            setScoreDialog(true);
                          }}
                        >
                          Score
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tracking Tab */}
        <TabPanel value={activeTab} index={4}>
          <Box p={2}>
            <Typography variant="h6" gutterBottom>Claude Code Tracking Overview</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Tracking data is automatically synced from the Claude Code hooks system.
              Click "Sync Tracking" to refresh the latest data.
            </Alert>
            <Grid container spacing={2}>
              {teams.map((team) => (
                <Grid item xs={12} md={6} key={team.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6">{team.team_name}</Typography>
                      <Divider sx={{ my: 1 }} />
                      {team.tracking_summary ? (
                        <Box>
                          <Typography variant="body2">
                            Total Prompts: {team.tracking_summary.total_prompts || 0}
                          </Typography>
                          <Typography variant="body2">
                            Tool Calls: {team.tracking_summary.total_tools || 0}
                          </Typography>
                          <Typography variant="body2">
                            Sessions: {team.tracking_summary.session_count || 0}
                          </Typography>
                          <Typography variant="body2">
                            Auto-approved: {team.tracking_summary.auto_approved_count || 0}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No tracking data yet
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Participants from Excel</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
            {importResult && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Imported {importResult.imported} participants.
                {importResult.skipped > 0 && ` Skipped: ${importResult.skipped}`}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!importFile || importing}
          >
            {importing ? <CircularProgress size={24} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Module Dialog */}
      <Dialog open={moduleDialog} onClose={() => setModuleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Module</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Module Name"
            value={newModule.name}
            onChange={(e) => setNewModule({ ...newModule, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={newModule.description}
            onChange={(e) => setNewModule({ ...newModule, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Complexity</InputLabel>
            <Select
              value={newModule.complexity_level}
              label="Complexity"
              onChange={(e) => setNewModule({ ...newModule, complexity_level: e.target.value })}
            >
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddModule}>Add Module</Button>
        </DialogActions>
      </Dialog>

      {/* KPI Dialog */}
      <Dialog open={kpiDialog} onClose={() => setKpiDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>KPI Configuration</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Total weight must equal 100%. Current: {kpiConfigs.reduce((sum, k) => sum + k.weight_percentage, 0)}%
          </Alert>
          {kpiConfigs.map((kpi, idx) => (
            <Box key={kpi.kpi_id} mb={2} p={2} border={1} borderColor="divider" borderRadius={1}>
              <Typography variant="subtitle1">{kpi.name}</Typography>
              <Typography variant="body2" color="text.secondary">{kpi.description}</Typography>
              <Box mt={2}>
                <Typography gutterBottom>Weight: {kpi.weight_percentage}%</Typography>
                <Slider
                  value={kpi.weight_percentage}
                  onChange={(e, v) => {
                    const newConfigs = [...kpiConfigs];
                    newConfigs[idx].weight_percentage = v;
                    setKpiConfigs(newConfigs);
                  }}
                  min={0}
                  max={100}
                  step={5}
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateKPIs}
            disabled={Math.abs(kpiConfigs.reduce((sum, k) => sum + k.weight_percentage, 0) - 100) > 0.01}
          >
            Save KPIs
          </Button>
        </DialogActions>
      </Dialog>

      {/* Score Dialog */}
      <Dialog open={scoreDialog} onClose={() => setScoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Score Team: {selectedTeam?.team_name}</DialogTitle>
        <DialogContent>
          {kpiConfigs.filter(k => !k.is_auto_calculated).map((kpi) => (
            <Box key={kpi.kpi_id} mb={3}>
              <Typography gutterBottom>
                {kpi.name} (Weight: {kpi.weight_percentage}%)
              </Typography>
              <Slider
                value={teamScores[kpi.kpi_id] || 0}
                onChange={(e, v) => setTeamScores({ ...teamScores, [kpi.kpi_id]: v })}
                min={0}
                max={kpi.max_score}
                step={0.5}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitScore}>Submit Score</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HackathonDashboard;
